import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  staffAttendanceMgmtAPI,
  StaffGridRow,
  MonthlyGridDay,
  StaffSalarySummary,
  RecordPaymentPayload,
} from '../../api/staffAttendanceManagement';
import { staffAPI, PaymentRequest } from '../../api/staff';
import toast from 'react-hot-toast';
import {
  Calendar,
  CalendarDays,
  Clock,
  CheckCircle2,
  AlertCircle,
  Eye,
  Plus,
  Trash2,
  Search,
  ChevronLeft,
  ChevronRight,
  User,
  MapPin,
  IndianRupee,
  Receipt,
  FileCheck,
  X,
  CreditCard,
  DollarSign,
  AlertTriangle,
  Sparkles,
  Check,
} from 'lucide-react';
import {
  formatWorkingDuration,
  formatDurationHMS,
  formatTimeAMPM,
  resolveUploadUrl,
} from '../../utils/helpers';
import {
  getIndianHolidaySuggestions,
  getMonthSundays,
  HolidaySuggestion,
} from '../../utils/indianHolidays';
import AttendanceEntryModal from '../../components/attendance/AttendanceEntryModal';
import ShiftsWeekOffsTab from '../../components/attendance/ShiftsWeekOffsTab';

export default function StaffAttendanceManagementPage() {
  const queryClient = useQueryClient();

  // Active Main Tab
  const [activeTab, setActiveTab] = useState<'attendance' | 'salary' | 'advances' | 'shifts'>('attendance');

  // Attendance v3: manual attendance / edit modal
  const [entryModal, setEntryModal] = useState<{
    mode: 'create' | 'edit';
    staffId?: number | null;
    staffName?: string;
    date?: string;
    dayItem?: MonthlyGridDay | null;
    salaryPaid?: boolean;
  } | null>(null);

  // Month navigation state YYYY-MM (default to current month)
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  });

  // Search filter
  const [searchQuery, setSearchQuery] = useState('');

  // Modals state
  const [isHolidaysModalOpen, setIsHolidaysModalOpen] = useState(false);
  const [selectedStaffForDrilldown, setSelectedStaffForDrilldown] = useState<StaffGridRow | null>(null);
  const [selectedStaffForPayment, setSelectedStaffForPayment] = useState<StaffSalarySummary | null>(null);
  const [viewPhotoUrl, setViewPhotoUrl] = useState<string | null>(null);
  const [viewLateRecordsStaff, setViewLateRecordsStaff] = useState<StaffSalarySummary | null>(null);

  // New Holiday form
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayDesc, setNewHolidayDesc] = useState('');

  // Payment Form State (adjustmentStr as string for free typing +, -, numbers without spinner arrows)
  const [paymentForm, setPaymentForm] = useState({
    adjustmentStr: '0',
    adjustment_reason: '',
    payment_mode: 'upi' as 'cash' | 'upi' | 'bank' | 'other',
    payment_reference: '',
    notes: '',
  });

  // Parsed numeric adjustment
  const currentAdjustmentNum = useMemo(() => {
    const raw = paymentForm.adjustmentStr.trim();
    if (raw === '' || raw === '+' || raw === '-') return 0;
    const val = parseFloat(raw);
    return isNaN(val) ? 0 : val;
  }, [paymentForm.adjustmentStr]);

  // Month navigation helpers
  const handlePrevMonth = () => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const prevDate = new Date(y, m - 2, 1);
    const newY = prevDate.getFullYear();
    const newM = String(prevDate.getMonth() + 1).padStart(2, '0');
    setSelectedMonth(`${newY}-${newM}`);
  };

  const handleNextMonth = () => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const nextDate = new Date(y, m, 1);
    const newY = nextDate.getFullYear();
    const newM = String(nextDate.getMonth() + 1).padStart(2, '0');
    setSelectedMonth(`${newY}-${newM}`);
  };

  const formatMonthLabel = (mStr: string) => {
    const [y, m] = mStr.split('-').map(Number);
    const d = new Date(y, m - 1, 1);
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // 1. Fetch Monthly Attendance Grid
  const { data: gridRes, isLoading: isGridLoading } = useQuery({
    queryKey: ['staffAttendanceGrid', selectedMonth],
    queryFn: () => staffAttendanceMgmtAPI.getMonthlyGrid(selectedMonth),
    enabled: activeTab === 'attendance',
  });
  const gridData = gridRes?.data;
  const staffGridList = gridData?.grid || [];
  const monthHolidays = gridData?.holidays || [];
  const totalMonthDays = gridData?.total_days || 30;

  // 2. Fetch Salary Summary
  const { data: salaryRes, isLoading: isSalaryLoading, refetch: refetchSalary } = useQuery({
    queryKey: ['staffSalarySummary', selectedMonth],
    queryFn: () => staffAttendanceMgmtAPI.getSalarySummary(selectedMonth),
    enabled: activeTab === 'salary',
  });
  const salaryData = salaryRes?.data;
  const staffSalaryList = salaryData?.staff_salaries || [];

  // 3. Fetch Advance / Payment Requests
  const { data: requestsRes, isLoading: isRequestsLoading, refetch: refetchRequests } = useQuery({
    queryKey: ['staffPaymentRequests'],
    queryFn: () => staffAPI.getPaymentRequests(),
    enabled: activeTab === 'advances',
  });
  const paymentRequests = (requestsRes?.data || []) as PaymentRequest[];

  // 4. Fetch Holidays list for modal
  const { data: holidaysRes, refetch: refetchHolidays } = useQuery({
    queryKey: ['staffHolidaysList', selectedMonth],
    queryFn: () => staffAttendanceMgmtAPI.getHolidays(selectedMonth),
    enabled: isHolidaysModalOpen,
  });
  const holidaysList = holidaysRes?.data || [];

  // Mutations
  const addHolidayMutation = useMutation({
    mutationFn: (payload: { holiday_date: string; description?: string }) =>
      staffAttendanceMgmtAPI.addHoliday(payload),
    onSuccess: () => {
      toast.success('Holiday date added successfully!');
      setNewHolidayDate('');
      setNewHolidayDesc('');
      refetchHolidays();
      queryClient.invalidateQueries({ queryKey: ['staffAttendanceGrid'] });
      queryClient.invalidateQueries({ queryKey: ['staffSalarySummary'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Failed to add holiday.');
    },
  });

  const deleteHolidayMutation = useMutation({
    mutationFn: (id: number) => staffAttendanceMgmtAPI.deleteHoliday(id),
    onSuccess: () => {
      toast.success('Holiday deleted.');
      refetchHolidays();
      queryClient.invalidateQueries({ queryKey: ['staffAttendanceGrid'] });
      queryClient.invalidateQueries({ queryKey: ['staffSalarySummary'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Failed to delete holiday.');
    },
  });

  const recordPaymentMutation = useMutation({
    mutationFn: (payload: RecordPaymentPayload) => staffAttendanceMgmtAPI.recordPayment(payload),
    onSuccess: () => {
      toast.success('Salary payment recorded successfully!');
      setSelectedStaffForPayment(null);
      refetchSalary();
      queryClient.invalidateQueries({ queryKey: ['staffAttendanceGrid'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Failed to record payment.');
    },
  });

  const approveRequestMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: { status: 'approved' | 'rejected'; notes?: string } }) =>
      staffAPI.approvePaymentRequest(id, payload),
    onSuccess: () => {
      toast.success('Request updated successfully!');
      refetchRequests();
      queryClient.invalidateQueries({ queryKey: ['staffSalarySummary'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Failed to update request.');
    },
  });

  // Filtered staff lists based on search
  const filteredGridList = useMemo(() => {
    if (!searchQuery.trim()) return staffGridList;
    const q = searchQuery.toLowerCase();
    return staffGridList.filter(
      (r) =>
        r.staff.full_name.toLowerCase().includes(q) ||
        r.staff.staff_code.toLowerCase().includes(q) ||
        r.staff.role.toLowerCase().includes(q)
    );
  }, [staffGridList, searchQuery]);

  const filteredSalaryList = useMemo(() => {
    if (!searchQuery.trim()) return staffSalaryList;
    const q = searchQuery.toLowerCase();
    return staffSalaryList.filter(
      (s) =>
        s.full_name.toLowerCase().includes(q) ||
        s.staff_code.toLowerCase().includes(q) ||
        s.role.toLowerCase().includes(q)
    );
  }, [staffSalaryList, searchQuery]);

  // Handle open payment modal
  const handleOpenPaymentModal = (staffSal: StaffSalarySummary) => {
    setSelectedStaffForPayment(staffSal);
    setPaymentForm({
      adjustmentStr: staffSal.adjustment ? String(staffSal.adjustment) : '0',
      adjustment_reason: staffSal.adjustment_reason || '',
      payment_mode: (staffSal.payment_details?.payment_mode as any) || 'upi',
      payment_reference: staffSal.payment_details?.payment_reference || '',
      notes: staffSal.payment_details?.notes || '',
    });
  };

  // Submit salary payment
  const handleConfirmPayment = () => {
    if (!selectedStaffForPayment) return;
    const adj = currentAdjustmentNum;
    const finalAmt = Math.max(
      0,
      +(selectedStaffForPayment.base_salary + adj - selectedStaffForPayment.advance_deducted).toFixed(2)
    );

    const payload: RecordPaymentPayload = {
      staff_id: selectedStaffForPayment.staff_id,
      month_year: selectedMonth,
      total_days: selectedStaffForPayment.total_days,
      present_days: selectedStaffForPayment.present_days,
      late_days: selectedStaffForPayment.late_days,
      absent_days: selectedStaffForPayment.absent_days,
      holiday_days: selectedStaffForPayment.holiday_days,
      base_salary: selectedStaffForPayment.base_salary,
      adjustment: adj,
      adjustment_reason: paymentForm.adjustment_reason || undefined,
      advance_deducted: selectedStaffForPayment.advance_deducted,
      final_amount: finalAmt,
      payment_mode: paymentForm.payment_mode,
      payment_reference: paymentForm.payment_reference || undefined,
      notes: paymentForm.notes || undefined,
      week_off_days: selectedStaffForPayment.week_off_days,
      extra_days: selectedStaffForPayment.extra_days,
      late_minutes: selectedStaffForPayment.late_minutes,
      late_deduction: selectedStaffForPayment.late_deduction,
      early_minutes: selectedStaffForPayment.early_minutes,
      early_deduction: selectedStaffForPayment.early_deduction,
      ot_minutes: selectedStaffForPayment.ot_minutes,
      ot_amount: selectedStaffForPayment.ot_amount,
    };

    recordPaymentMutation.mutate(payload);
  };

  // Suggested Indian festivals & Sundays for the selected month
  const suggestedFestivals = useMemo(() => {
    return getIndianHolidaySuggestions(selectedMonth);
  }, [selectedMonth]);

  const monthSundays = useMemo(() => {
    return getMonthSundays(selectedMonth);
  }, [selectedMonth]);

  const existingHolidayDates = useMemo(() => {
    return new Set(holidaysList.map((h: any) => h.holiday_date));
  }, [holidaysList]);

  // Handler to add all Sundays of the month
  const handleAddAllSundays = async () => {
    const unadded = monthSundays.filter((s) => !existingHolidayDates.has(s.date));
    if (unadded.length === 0) {
      toast.success('All Sundays in this month are already added.');
      return;
    }
    try {
      for (const sun of unadded) {
        await staffAttendanceMgmtAPI.addHoliday({ holiday_date: sun.date, description: sun.name });
      }
      toast.success(`Added ${unadded.length} Sunday(s) as holidays!`);
      refetchHolidays();
      queryClient.invalidateQueries({ queryKey: ['staffAttendanceGrid'] });
      queryClient.invalidateQueries({ queryKey: ['staffSalarySummary'] });
    } catch {
      toast.error('Failed to add some Sundays.');
    }
  };

  return (
    <div className="w-full min-h-screen bg-white text-black font-sans pb-16">
      {/* Top Header */}
      <div className="border-b border-gray-200 bg-white py-4 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center space-x-2.5">
              <div className="p-2 bg-red-600 text-white rounded-md">
                <CalendarDays className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-black tracking-tight text-black">
                  Staff Attendance & Payments
                </h1>
                <p className="text-xs text-gray-700 font-semibold">
                  Unified monthly attendance tracking, live geo-fence records, manual holidays & salary disbursement
                </p>
              </div>
            </div>
          </div>

          {/* Month Navigator Controls */}
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrevMonth}
              className="p-2 bg-white hover:bg-gray-100 text-black border border-gray-300 rounded-md transition-all cursor-pointer"
              title="Previous Month"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="px-4 py-2 bg-white border border-gray-300 rounded-md font-mono font-black text-xs sm:text-sm text-black min-w-[150px] text-center">
              {formatMonthLabel(selectedMonth)}
            </div>
            <button
              onClick={handleNextMonth}
              className="p-2 bg-white hover:bg-gray-100 text-black border border-gray-300 rounded-md transition-all cursor-pointer"
              title="Next Month"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              id="open-mark-attendance"
              onClick={() => setEntryModal({ mode: 'create' })}
              className="px-3.5 py-2 bg-black hover:bg-gray-800 text-white text-xs font-black rounded-md transition-all flex items-center space-x-1.5 cursor-pointer ml-1"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Mark Attendance</span>
            </button>
            <button
              onClick={() => setIsHolidaysModalOpen(true)}
              className="px-3.5 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-black rounded-md transition-all flex items-center space-x-1.5 cursor-pointer ml-1"
            >
              <Calendar className="w-3.5 h-3.5" />
              <span>Holidays ({monthHolidays.length})</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-5 space-y-5">
        {/* Navigation Tabs */}
        <div className="flex items-center justify-between border-b border-gray-200">
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveTab('attendance')}
              className={`pb-3 px-4 font-black text-xs sm:text-sm transition-all border-b-2 cursor-pointer ${
                activeTab === 'attendance'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-700 hover:text-black'
              }`}
            >
              Attendance Matrix & Grid
            </button>
            <button
              onClick={() => setActiveTab('salary')}
              className={`pb-3 px-4 font-black text-xs sm:text-sm transition-all border-b-2 cursor-pointer ${
                activeTab === 'salary'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-700 hover:text-black'
              }`}
            >
              Salary Calculation & Payouts
            </button>
            <button
              onClick={() => setActiveTab('advances')}
              className={`pb-3 px-4 font-black text-xs sm:text-sm transition-all border-b-2 cursor-pointer ${
                activeTab === 'advances'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-700 hover:text-black'
              }`}
            >
              Advance Requests
            </button>
            <button
              id="tab-shifts"
              onClick={() => setActiveTab('shifts')}
              className={`pb-3 px-4 font-black text-xs sm:text-sm transition-all border-b-2 cursor-pointer ${
                activeTab === 'shifts'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-700 hover:text-black'
              }`}
            >
              Shifts & Week-Offs
            </button>
          </div>

          {/* Search Bar */}
          <div className="hidden sm:flex items-center relative mb-2">
            <Search className="w-3.5 h-3.5 absolute left-2.5 text-gray-500" />
            <input
              type="text"
              placeholder="Search staff..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs bg-white border border-gray-300 rounded-md text-black font-semibold placeholder:text-gray-500 focus:outline-none focus:border-red-600"
            />
          </div>
        </div>

        {/* TAB 1: ATTENDANCE MATRIX & GRID */}
        {activeTab === 'attendance' && (
          <div className="space-y-4">
            {/* Quick Stats Banner */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white p-3.5 border border-gray-300 rounded-md">
                <span className="text-gray-700 text-xs font-bold block">Total Staff Members</span>
                <span className="text-xl font-black text-black font-mono mt-0.5 block">{staffGridList.length}</span>
              </div>
              <div className="bg-white p-3.5 border border-gray-300 rounded-md">
                <span className="text-gray-700 text-xs font-bold block">Days in Month</span>
                <span className="text-xl font-black text-black font-mono mt-0.5 block">{totalMonthDays}</span>
              </div>
              <div className="bg-white p-3.5 border border-gray-300 rounded-md">
                <span className="text-gray-700 text-xs font-bold block">Holidays Defined</span>
                <span className="text-xl font-black text-red-600 font-mono mt-0.5 block">{monthHolidays.length}</span>
              </div>
              <div className="bg-white p-3.5 border border-gray-300 rounded-md">
                <span className="text-gray-700 text-xs font-bold block">Geo-Fence Boundary</span>
                <span className="text-xl font-black text-black font-mono mt-0.5 block">30m Studio Radius</span>
              </div>
            </div>

            {/* Legend & Instructions */}
            <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 bg-gray-50 border border-gray-200 rounded-md text-xs font-bold text-gray-800">
              <div className="flex items-center space-x-3">
                <span className="text-black font-black">Legend:</span>
                <span className="flex items-center space-x-1">
                  <span className="w-3 h-3 rounded-full bg-emerald-600 inline-block" />
                  <span>Present</span>
                </span>
                <span className="flex items-center space-x-1">
                  <span className="w-3 h-3 rounded-full bg-amber-500 inline-block" />
                  <span>Late (after shift + grace)</span>
                </span>
                <span className="flex items-center space-x-1">
                  <span className="w-3 h-3 rounded-full bg-rose-600 inline-block" />
                  <span>Absent</span>
                </span>
                <span className="flex items-center space-x-1">
                  <span className="w-3 h-3 rounded-full bg-blue-600 inline-block" />
                  <span>Holiday</span>
                </span>
                <span className="flex items-center space-x-1">
                  <span className="w-3 h-3 rounded-full bg-purple-600 inline-block" />
                  <span>Week-Off (W)</span>
                </span>
                <span className="flex items-center space-x-1">
                  <span className="w-3 h-3 rounded-full bg-cyan-600 inline-block" />
                  <span>Half-Day</span>
                </span>
                <span className="flex items-center space-x-1">
                  <span className="w-3 h-3 rounded-full bg-indigo-600 inline-block" />
                  <span>Leave</span>
                </span>
                <span className="flex items-center space-x-1">
                  <span className="w-3 h-3 rounded ring-2 ring-orange-500 inline-block" />
                  <span>Missing checkout</span>
                </span>
                <span className="flex items-center space-x-1">
                  <span className="w-2 h-2 rounded-full bg-fuchsia-600 inline-block" />
                  <span>Manual / Edited</span>
                </span>
              </div>
              <div className="text-gray-600 text-[11px]">
                Tip: Click a day cell to mark or edit attendance. Click the staff name to view selfies & details.
              </div>
            </div>

            {/* Attendance Matrix Table */}
            <div className="border border-gray-300 rounded-md bg-white overflow-hidden">
              {isGridLoading ? (
                <div className="text-center py-16 font-bold text-gray-600 animate-pulse text-sm">
                  Loading attendance matrix for {formatMonthLabel(selectedMonth)}...
                </div>
              ) : filteredGridList.length === 0 ? (
                <div className="text-center py-16 font-bold text-gray-700 text-sm">
                  No staff members found.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-gray-100 border-b border-gray-300 text-black font-black uppercase text-[10px] tracking-wider">
                        <th className="py-2.5 px-3 min-w-[180px] sticky left-0 bg-gray-100 z-10 border-r border-gray-300">Staff Member</th>
                        <th className="py-2.5 px-2 text-center border-r border-gray-300">Role</th>
                        <th className="py-2.5 px-2 text-center border-r border-gray-300 text-emerald-700">Present</th>
                        <th className="py-2.5 px-2 text-center border-r border-gray-300 text-amber-700">Late</th>
                        <th className="py-2.5 px-2 text-center border-r border-gray-300 text-rose-700">Absent</th>
                        <th className="py-2.5 px-2 text-center border-r border-gray-300 text-blue-700">Holidays</th>
                        <th className="py-2.5 px-2 text-center border-r border-gray-300 min-w-[95px]">Hours</th>
                        {/* Day Columns 1..N */}
                        {Array.from({ length: totalMonthDays }, (_, i) => i + 1).map((dayNum) => (
                          <th
                            key={dayNum}
                            className="py-2 px-1 text-center font-mono min-w-[28px] border-r border-gray-200"
                          >
                            {dayNum}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredGridList.map((row) => (
                        <tr
                          key={row.staff.id}
                          onClick={() => setSelectedStaffForDrilldown(row)}
                          className="hover:bg-red-50/50 transition-all cursor-pointer group"
                        >
                          {/* Staff Column (Sticky) */}
                          <td className="py-2 px-3 sticky left-0 bg-white group-hover:bg-red-50/50 z-10 border-r border-gray-300">
                            <div className="flex items-center space-x-2">
                              <div className="w-7 h-7 rounded bg-red-100 border border-red-200 flex items-center justify-center font-black text-red-600 text-xs shrink-0 overflow-hidden">
                                {row.staff.profile_picture ? (
                                  <img
                                    src={resolveUploadUrl(row.staff.profile_picture)}
                                    alt={row.staff.full_name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  row.staff.full_name.charAt(0)
                                )}
                              </div>
                              <div className="min-w-0">
                                <div className="font-black text-black truncate max-w-[120px]">{row.staff.full_name}</div>
                                <div className="text-[10px] text-gray-500 font-mono">{row.staff.staff_code}</div>
                                {row.shift_set === false && (
                                  <div className="text-[9px] font-black text-amber-700">Shift not set</div>
                                )}
                                {!!row.missing_checkout_count && (
                                  <div className="text-[9px] font-black text-orange-600">{row.missing_checkout_count} missing checkout</div>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Role */}
                          <td className="py-2 px-2 text-center border-r border-gray-300 font-bold uppercase text-[10px]">
                            {row.staff.role}
                          </td>

                          {/* Counts */}
                          <td className="py-2 px-2 text-center border-r border-gray-300 font-mono font-black text-emerald-700">
                            {row.present_count}
                          </td>
                          <td className="py-2 px-2 text-center border-r border-gray-300 font-mono font-black text-amber-700">
                            {row.late_count}
                          </td>
                          <td className="py-2 px-2 text-center border-r border-gray-300 font-mono font-black text-rose-700">
                            {row.absent_count}
                          </td>
                          <td className="py-2 px-2 text-center border-r border-gray-300 font-mono font-black text-blue-700">
                            {row.holiday_count}
                          </td>
                          <td className="py-2 px-2 text-center border-r border-gray-300 font-mono text-[11px] whitespace-nowrap">
                            {row.total_seconds && row.total_seconds > 0 ? (
                              <span className="font-black text-black">{formatDurationHMS(row.total_seconds, true)}</span>
                            ) : row.total_hours > 0 ? (
                              <span className="font-black text-black">{formatDurationHMS(row.total_hours, false)}</span>
                            ) : (
                              <span className="font-bold text-gray-400">0h 0m 0s</span>
                            )}
                          </td>

                          {/* Daily Status Cells */}
                          {row.days.map((dayItem: MonthlyGridDay) => {
                            let cellBg = 'bg-gray-100 text-gray-400';
                            let cellText = '·';
                            const offAndWorked = (dayItem.is_holiday || dayItem.is_week_off) && !!dayItem.attendance_id &&
                              ['present', 'late', 'half_day'].includes(dayItem.status);

                            if (dayItem.status === 'present') {
                              cellBg = 'bg-emerald-600 text-white font-black';
                              cellText = 'P';
                            } else if (dayItem.status === 'late') {
                              cellBg = 'bg-amber-500 text-white font-black';
                              cellText = 'L';
                            } else if (dayItem.status === 'half_day') {
                              cellBg = 'bg-cyan-600 text-white font-black';
                              cellText = '½';
                            } else if (dayItem.status === 'leave') {
                              cellBg = dayItem.leave_paid ? 'bg-indigo-600 text-white font-black' : 'bg-indigo-300 text-indigo-900 font-black';
                              cellText = 'LV';
                            } else if (dayItem.is_holiday || dayItem.status === 'holiday') {
                              cellBg = 'bg-blue-600 text-white font-black';
                              cellText = 'H';
                            } else if (dayItem.is_week_off || dayItem.status === 'week_off') {
                              cellBg = 'bg-purple-600 text-white font-black';
                              cellText = 'W';
                            } else if (dayItem.status === 'absent') {
                              cellBg = 'bg-rose-600 text-white font-black';
                              cellText = 'A';
                            }
                            if (dayItem.missing_checkout) cellBg += ' ring-2 ring-orange-500';
                            if (offAndWorked) cellBg += ' outline outline-2 outline-purple-400';

                            const todayLocal = new Date();
                            const todayKey = `${todayLocal.getFullYear()}-${String(todayLocal.getMonth() + 1).padStart(2, '0')}-${String(todayLocal.getDate()).padStart(2, '0')}`;
                            const canClick = !!dayItem.attendance_id || dayItem.date <= todayKey;

                            const tipParts = [
                              `${dayItem.date}: ${dayItem.status.toUpperCase()}`,
                              dayItem.check_in_time ? `In ${formatTimeAMPM(dayItem.check_in_time)}` : '',
                              dayItem.check_out_time ? `Out ${formatTimeAMPM(dayItem.check_out_time)}` : '',
                              dayItem.late_min ? `Late ${dayItem.late_min}m` : '',
                              dayItem.early_min ? `Early ${dayItem.early_min}m` : '',
                              dayItem.ot_min ? `OT ${dayItem.ot_min}m` : '',
                              dayItem.missing_checkout ? 'MISSING CHECKOUT' : '',
                              offAndWorked ? 'Worked on off-day (+1 day)' : '',
                              dayItem.is_manual ? 'Manual entry' : '',
                              dayItem.is_edited ? 'Edited by Admin' : '',
                            ].filter(Boolean);

                            return (
                              <td
                                key={dayItem.day}
                                className="p-1 text-center border-r border-gray-200"
                                title={tipParts.join(' • ')}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!canClick) return;
                                  setEntryModal({
                                    mode: dayItem.attendance_id ? 'edit' : 'create',
                                    staffId: row.staff.id,
                                    staffName: row.staff.full_name,
                                    date: dayItem.date,
                                    dayItem,
                                    salaryPaid: !!row.salary_paid,
                                  });
                                }}
                              >
                                <div
                                  className={`relative w-5 h-5 mx-auto rounded flex items-center justify-center text-[9px] ${cellBg} ${canClick ? 'hover:scale-110 transition-transform' : ''}`}
                                >
                                  {cellText}
                                  {(dayItem.is_manual || dayItem.is_edited) && (
                                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-fuchsia-600 border border-white" />
                                  )}
                                  {!!dayItem.ot_min && (
                                    <span className="absolute -bottom-1 -right-1 w-2 h-2 rounded-full bg-lime-500 border border-white" />
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 2: SALARY CALCULATION & PAYOUTS */}
        {activeTab === 'salary' && (
          <div className="space-y-4">
            {/* Calculation Overview Banner */}
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs space-y-1">
              <div className="font-black text-red-700 flex items-center space-x-1">
                <Receipt className="w-4 h-4 text-red-600" />
                <span>Salary Calculation Formula:</span>
              </div>
              <p className="text-gray-800 font-semibold leading-relaxed">
                Per-Day Rate = <code>Monthly Salary ÷ Total Days ({totalMonthDays})</code> (daily-wage staff: their daily amount) • Per-minute rate ={' '}
                <code>Per-Day Rate ÷ Shift Minutes</code> • Late (after grace) & early checkout deducted per minute (max 1 day) • Overtime paid per minute once it reaches the minimum •
                Week-offs & holidays are paid; working on them = <strong>extra 1 day pay</strong> • Final Amount ={' '}
                <code>Calculated Salary + Adjustment - Unpaid Advance</code>.
              </p>
            </div>

            {/* Salary Summary Table */}
            <div className="border border-gray-300 rounded-md bg-white overflow-hidden">
              {isSalaryLoading ? (
                <div className="text-center py-16 font-bold text-gray-600 animate-pulse text-sm">
                  Calculating salary figures for {formatMonthLabel(selectedMonth)}...
                </div>
              ) : filteredSalaryList.length === 0 ? (
                <div className="text-center py-16 font-bold text-gray-700 text-sm">
                  No staff salary records found for this month.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-gray-100 border-b border-gray-300 text-black font-black uppercase text-[10px] tracking-wider">
                        <th className="py-2.5 px-3">Staff</th>
                        <th className="py-2.5 px-2 text-right">Monthly Sal</th>
                        <th className="py-2.5 px-2 text-right">Per-Day Rate</th>
                        <th className="py-2.5 px-2 text-center">Worked</th>
                        <th className="py-2.5 px-2 text-center">Holidays</th>
                        <th className="py-2.5 px-2 text-center">Week-Off</th>
                        <th className="py-2.5 px-2 text-center">Absent</th>
                        <th className="py-2.5 px-2 text-center">Late In</th>
                        <th className="py-2.5 px-2 text-right">Late/Early Cut</th>
                        <th className="py-2.5 px-2 text-right">OT / Extra Day</th>
                        <th className="py-2.5 px-2 text-right">Base Amount</th>
                        <th className="py-2.5 px-2 text-right">Advance Cut</th>
                        <th className="py-2.5 px-2 text-right">Adjust (+/-)</th>
                        <th className="py-2.5 px-3 text-right font-black text-red-600">Final Payable</th>
                        <th className="py-2.5 px-3 text-center">Status / Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredSalaryList.map((item) => (
                        <tr key={item.staff_id} className="hover:bg-gray-50 transition-all">
                          <td className="py-2.5 px-3 font-black text-black">
                            <div>{item.full_name}</div>
                            <div className="text-[10px] text-gray-500 font-mono">{item.staff_code} • {item.role}{item.salary_type === 'daily' ? ' • daily wage' : ''}</div>
                            {item.shift_set === false && (
                              <span className="inline-block mt-0.5 px-1.5 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 rounded text-[9px] font-black">Shift not set</span>
                            )}
                            {!!item.missing_checkout_days && (
                              <span className="inline-block mt-0.5 ml-1 px-1.5 py-0.5 bg-orange-100 text-orange-800 border border-orange-300 rounded text-[9px] font-black">{item.missing_checkout_days} missing checkout</span>
                            )}
                          </td>
                          <td className="py-2.5 px-2 text-right font-mono font-bold text-black">
                            ₹{item.monthly_salary.toLocaleString()}
                          </td>
                          <td className="py-2.5 px-2 text-right font-mono text-gray-700 font-bold">
                            ₹{item.daily_rate.toLocaleString()}
                          </td>
                          <td className="py-2.5 px-2 text-center font-mono font-black text-emerald-700">
                            {item.present_days}
                          </td>
                          <td className="py-2.5 px-2 text-center font-mono font-black text-blue-700">
                            {item.holiday_days}
                          </td>
                          <td className="py-2.5 px-2 text-center font-mono font-black text-purple-700">
                            {item.week_off_days ?? 0}
                          </td>
                          <td className="py-2.5 px-2 text-center font-mono font-black text-rose-700">
                            {item.absent_days}
                          </td>
                          <td className="py-2.5 px-2 text-center font-mono">
                            {item.late_days > 0 ? (
                              <button
                                onClick={() => setViewLateRecordsStaff(item)}
                                className="px-2 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 rounded font-black text-[10px] hover:bg-amber-200 cursor-pointer"
                              >
                                {item.late_days} Late
                              </button>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </td>
                          <td className="py-2.5 px-2 text-right font-mono font-bold text-rose-700 whitespace-nowrap">
                            {(item.late_deduction || 0) + (item.early_deduction || 0) > 0 ? (
                              <div>
                                <div>-₹{((item.late_deduction || 0) + (item.early_deduction || 0)).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                                <div className="text-[9px] text-gray-500">{item.late_minutes || 0}m late • {item.early_minutes || 0}m early</div>
                              </div>
                            ) : <span className="text-gray-400">₹0</span>}
                          </td>
                          <td className="py-2.5 px-2 text-right font-mono font-bold text-emerald-700 whitespace-nowrap">
                            {(item.ot_amount || 0) > 0 || (item.extra_days || 0) > 0 ? (
                              <div>
                                {(item.ot_amount || 0) > 0 && <div>+₹{(item.ot_amount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-[9px] text-gray-500">({Math.floor((item.ot_minutes || 0) / 60)}h {(item.ot_minutes || 0) % 60}m OT)</span></div>}
                                {(item.extra_days || 0) > 0 && <div className="text-purple-700">+{item.extra_days} extra day</div>}
                              </div>
                            ) : <span className="text-gray-400">₹0</span>}
                          </td>
                          <td className="py-2.5 px-2 text-right font-mono font-black text-black">
                            ₹{item.base_salary.toLocaleString()}
                          </td>
                          <td className="py-2.5 px-2 text-right font-mono font-bold text-rose-700">
                            {item.advance_deducted > 0 ? `-₹${item.advance_deducted.toLocaleString()}` : '₹0'}
                          </td>
                          <td className="py-2.5 px-2 text-right font-mono font-bold">
                            {item.adjustment !== 0 ? (
                              <span className={item.adjustment > 0 ? 'text-emerald-700' : 'text-rose-700'}>
                                {item.adjustment > 0 ? `+₹${item.adjustment}` : `-₹${Math.abs(item.adjustment)}`}
                              </span>
                            ) : (
                              <span className="text-gray-400">₹0</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono font-black text-red-600 text-sm">
                            ₹{item.final_amount.toLocaleString()}
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            {item.is_paid ? (
                              <div className="inline-flex items-center space-x-1 px-2.5 py-1 bg-emerald-100 border border-emerald-300 text-emerald-900 rounded text-[11px] font-black">
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                                <span>PAID ({item.payment_details?.payment_mode?.toUpperCase()})</span>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleOpenPaymentModal(item)}
                                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white font-black text-xs rounded transition-all cursor-pointer"
                              >
                                Mark as Paid
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 4: SHIFTS & WEEK-OFFS (Attendance v3) */}
        {activeTab === 'shifts' && <ShiftsWeekOffsTab searchQuery={searchQuery} />}

        {/* TAB 3: ADVANCE REQUESTS */}
        {activeTab === 'advances' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-black text-black">Staff Payment & Advance Requests</h3>
                <p className="text-xs text-gray-700 font-semibold">
                  Requests submitted by staff members. Approved advances are automatically deducted from monthly salary.
                </p>
              </div>
            </div>

            <div className="border border-gray-300 rounded-md bg-white overflow-hidden">
              {isRequestsLoading ? (
                <div className="text-center py-16 font-bold text-gray-600 animate-pulse text-sm">
                  Loading advance requests...
                </div>
              ) : paymentRequests.length === 0 ? (
                <div className="text-center py-16 font-bold text-gray-700 text-sm">
                  No payment or advance requests found.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-gray-100 border-b border-gray-300 text-black font-black uppercase text-[10px] tracking-wider">
                        <th className="py-2.5 px-3">Date</th>
                        <th className="py-2.5 px-3">Staff</th>
                        <th className="py-2.5 px-2">Type</th>
                        <th className="py-2.5 px-3">Reason</th>
                        <th className="py-2.5 px-3 text-right">Amount</th>
                        <th className="py-2.5 px-3 text-center">Status</th>
                        <th className="py-2.5 px-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {paymentRequests.map((req) => (
                        <tr key={req.id} className="hover:bg-gray-50 transition-all">
                          <td className="py-2.5 px-3 font-mono text-gray-600">
                            {new Date(req.created_at).toLocaleDateString('en-US', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </td>
                          <td className="py-2.5 px-3 font-black text-black">
                            {req.staff_name || `Staff #${req.staff_id}`}
                            <span className="block text-[10px] text-gray-500 font-mono">{req.staff_code}</span>
                          </td>
                          <td className="py-2.5 px-2 font-bold uppercase text-[10px]">
                            <span className="px-2 py-0.5 bg-gray-100 border border-gray-300 rounded">
                              {req.request_type}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-gray-800 font-semibold max-w-xs truncate">
                            {req.reason}
                            {req.notes && <span className="block text-[10px] text-gray-500">{req.notes}</span>}
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono font-black text-sm text-red-600">
                            ₹{Number(req.amount).toLocaleString()}
                          </td>
                          <td className="py-2.5 px-3 text-center font-bold">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${
                                req.status === 'approved'
                                  ? 'bg-emerald-100 text-emerald-900 border border-emerald-300'
                                  : req.status === 'rejected'
                                  ? 'bg-rose-100 text-rose-900 border border-rose-300'
                                  : 'bg-amber-100 text-amber-900 border border-amber-300'
                              }`}
                            >
                              {req.status}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            {req.status === 'pending' ? (
                              <div className="flex items-center justify-center space-x-1.5">
                                <button
                                  onClick={() =>
                                    approveRequestMutation.mutate({
                                      id: req.id,
                                      payload: { status: 'approved' },
                                    })
                                  }
                                  className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs rounded transition-all cursor-pointer"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() =>
                                    approveRequestMutation.mutate({
                                      id: req.id,
                                      payload: { status: 'rejected' },
                                    })
                                  }
                                  className="px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white font-black text-xs rounded transition-all cursor-pointer"
                                >
                                  Reject
                                </button>
                              </div>
                            ) : (
                              <span className="text-[11px] text-gray-500 font-semibold">
                                {req.approved_by_name ? `By ${req.approved_by_name}` : 'Processed'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {/* MODAL 0: MANUAL ATTENDANCE / EDIT (Attendance v3) */}
      {entryModal && (
        <AttendanceEntryModal
          mode={entryModal.mode}
          staffOptions={
            staffGridList.length > 0
              ? staffGridList.map((r) => ({ id: r.staff.id, full_name: r.staff.full_name, staff_code: r.staff.staff_code }))
              : (salaryRes?.data?.staff_salaries || []).map((s) => ({ id: s.staff_id, full_name: s.full_name, staff_code: s.staff_code }))
          }
          initialStaffId={entryModal.staffId ?? null}
          initialDate={entryModal.date}
          dayItem={entryModal.dayItem ?? null}
          staffName={entryModal.staffName}
          salaryPaid={entryModal.salaryPaid}
          onClose={() => setEntryModal(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['staffAttendanceGrid'] });
            queryClient.invalidateQueries({ queryKey: ['staffSalarySummary'] });
          }}
        />
      )}

      {/* MODAL 1: HOLIDAYS MANAGEMENT */}
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {isHolidaysModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="relative max-w-xl w-full bg-white border border-gray-300 rounded-md p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-200 pb-3">
              <div>
                <h3 className="text-base font-black text-black flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-red-600" />
                  <span>Manage Month Holidays</span>
                </h3>
                <p className="text-xs text-gray-600 font-semibold">
                  Exempted from salary cuts. Select from Indian & Hindu calendar suggestions below or add manually.
                </p>
              </div>
              <button
                onClick={() => setIsHolidaysModalOpen(false)}
                className="p-1 text-gray-500 hover:text-black cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Indian & Hindu Calendar Suggestions Section */}
            <div className="p-3.5 bg-red-50/60 border border-red-200 rounded-md space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1.5 text-xs font-black text-red-700">
                  <Sparkles className="w-4 h-4 text-red-600" />
                  <span>Suggested Indian & Hindu Holidays ({formatMonthLabel(selectedMonth)})</span>
                </div>
                <button
                  type="button"
                  onClick={handleAddAllSundays}
                  className="px-2.5 py-1 text-[11px] font-black bg-white hover:bg-gray-100 text-black border border-gray-300 rounded transition-all flex items-center space-x-1 cursor-pointer"
                  title="Add all Sundays of this month as holidays"
                >
                  <Plus className="w-3 h-3 text-red-600" />
                  <span>Add All Sundays ({monthSundays.length})</span>
                </button>
              </div>

              {suggestedFestivals.length === 0 ? (
                <p className="text-xs text-gray-600 font-medium">
                  No major national/festival holiday in standard calendar for this month. You can add custom dates below or use "Add All Sundays".
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-2 max-h-52 overflow-y-auto pr-1">
                  {suggestedFestivals.map((fest: HolidaySuggestion) => {
                    const isAlreadyAdded = existingHolidayDates.has(fest.date);
                    const festDateObj = new Date(fest.date);
                    const dateFormatted = festDateObj.toLocaleDateString('en-US', {
                      day: 'numeric',
                      month: 'short',
                      weekday: 'short',
                    });

                    return (
                      <div
                        key={fest.date + fest.name}
                        className="p-2.5 bg-white border border-gray-200 rounded flex items-center justify-between gap-2 text-xs"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className="font-mono font-black text-black">{dateFormatted}</span>
                            <span className="px-1.5 py-0.5 bg-red-100 text-red-800 rounded text-[9px] font-bold uppercase">
                              {fest.category}
                            </span>
                          </div>
                          <p className="text-gray-900 font-bold truncate mt-0.5" title={fest.name}>
                            {fest.name}
                          </p>
                        </div>

                        <div>
                          {isAlreadyAdded ? (
                            <span className="inline-flex items-center space-x-1 px-2.5 py-1 bg-emerald-100 text-emerald-800 border border-emerald-300 rounded font-black text-[10px]">
                              <Check className="w-3 h-3 text-emerald-700" />
                              <span>Added</span>
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                addHolidayMutation.mutate({
                                  holiday_date: fest.date,
                                  description: fest.name,
                                });
                              }}
                              disabled={addHolidayMutation.isPending}
                              className="px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded font-black text-[11px] flex items-center space-x-1 transition-all cursor-pointer"
                            >
                              <Plus className="w-3 h-3" />
                              <span>Pick & Add</span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Add Holiday Form (Manual) */}
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-md space-y-2.5">
              <span className="text-xs font-black text-black block">Or Add Custom Holiday Date Manually</span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] font-bold text-gray-700 block mb-1">Date</label>
                  <input
                    type="date"
                    value={newHolidayDate}
                    onChange={(e) => setNewHolidayDate(e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white border border-gray-300 rounded text-xs font-mono font-bold text-black focus:outline-none focus:border-red-600"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-700 block mb-1">Description</label>
                  <input
                    type="text"
                    placeholder="e.g. Studio Foundation Day"
                    value={newHolidayDesc}
                    onChange={(e) => setNewHolidayDesc(e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white border border-gray-300 rounded text-xs font-semibold text-black focus:outline-none focus:border-red-600"
                  />
                </div>
              </div>
              <button
                onClick={() => {
                  if (!newHolidayDate) {
                    toast.error('Please pick a date.');
                    return;
                  }
                  addHolidayMutation.mutate({
                    holiday_date: newHolidayDate,
                    description: newHolidayDesc || undefined,
                  });
                }}
                disabled={addHolidayMutation.isPending}
                className="w-full py-2 bg-red-600 hover:bg-red-700 text-white font-black text-xs rounded transition-all flex items-center justify-center space-x-1.5 cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Save Holiday Date</span>
              </button>
            </div>

            {/* Holidays List */}
            <div className="space-y-2">
              <span className="text-xs font-black text-black block">
                Saved Holidays for {formatMonthLabel(selectedMonth)} ({holidaysList.length})
              </span>
              {holidaysList.length === 0 ? (
                <div className="text-center py-5 text-xs text-gray-500 font-bold border border-dashed border-gray-300 rounded">
                  No holidays entered for this month yet. Pick from suggestions above or add manually.
                </div>
              ) : (
                <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                  {holidaysList.map((h: any) => (
                    <div
                      key={h.id}
                      className="p-2.5 bg-white border border-gray-200 rounded flex items-center justify-between text-xs"
                    >
                      <div>
                        <span className="font-mono font-black text-black">{h.holiday_date}</span>
                        {h.description && (
                          <span className="text-gray-700 font-semibold ml-2">({h.description})</span>
                        )}
                      </div>
                      <button
                        onClick={() => deleteHolidayMutation.mutate(h.id)}
                        disabled={deleteHolidayMutation.isPending}
                        className="p-1 text-gray-400 hover:text-rose-600 cursor-pointer"
                        title="Remove Holiday"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {/* MODAL 2: STAFF DRILLDOWN MODAL */}
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {selectedStaffForDrilldown && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="relative max-w-2xl w-full bg-white border border-gray-300 rounded-md p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-200 pb-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded bg-red-100 border border-red-200 flex items-center justify-center font-black text-red-600 text-sm overflow-hidden">
                  {selectedStaffForDrilldown.staff.profile_picture ? (
                    <img
                      src={resolveUploadUrl(selectedStaffForDrilldown.staff.profile_picture)}
                      alt={selectedStaffForDrilldown.staff.full_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    selectedStaffForDrilldown.staff.full_name.charAt(0)
                  )}
                </div>
                <div>
                  <h3 className="text-base font-black text-black">
                    {selectedStaffForDrilldown.staff.full_name}
                  </h3>
                  <p className="text-xs text-gray-600 font-mono font-bold">
                    {selectedStaffForDrilldown.staff.staff_code} • {selectedStaffForDrilldown.staff.role} • {formatMonthLabel(selectedMonth)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedStaffForDrilldown(null)}
                className="p-1 text-gray-500 hover:text-black cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Attendance Summary Strip */}
            <div className="grid grid-cols-4 gap-2 text-center text-xs">
              <div className="p-2 bg-gray-50 border border-gray-200 rounded">
                <span className="text-gray-600 font-bold block text-[11px]">Present Days</span>
                <span className="font-mono font-black text-emerald-700 text-sm">{selectedStaffForDrilldown.present_count}</span>
              </div>
              <div className="p-2 bg-gray-50 border border-gray-200 rounded">
                <span className="text-gray-600 font-bold block text-[11px]">Late Days</span>
                <span className="font-mono font-black text-amber-700 text-sm">{selectedStaffForDrilldown.late_count}</span>
              </div>
              <div className="p-2 bg-gray-50 border border-gray-200 rounded">
                <span className="text-gray-600 font-bold block text-[11px]">Absent Days</span>
                <span className="font-mono font-black text-rose-700 text-sm">{selectedStaffForDrilldown.absent_count}</span>
              </div>
              <div className="p-2 bg-gray-50 border border-gray-200 rounded">
                <span className="text-gray-600 font-bold block text-[11px]">Total Time</span>
                <span className="font-mono font-black text-black text-sm">
                  {formatDurationHMS(
                    selectedStaffForDrilldown.total_seconds ?? selectedStaffForDrilldown.total_hours,
                    Boolean(selectedStaffForDrilldown.total_seconds)
                  )}
                </span>
              </div>
            </div>

            {/* Daily Records List */}
            <div className="space-y-2">
              <span className="text-xs font-black text-black block">Day-by-Day Log Entries</span>
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {selectedStaffForDrilldown.days.map((dayItem: MonthlyGridDay) => (
                  <div
                    key={dayItem.day}
                    className="p-3 bg-gray-50 border border-gray-200 rounded flex items-center justify-between text-xs"
                  >
                    <div className="flex items-center space-x-3">
                      {/* Photo Thumbnail */}
                      {dayItem.check_in_photo ? (
                        <button
                          onClick={() => setViewPhotoUrl(resolveUploadUrl(dayItem.check_in_photo))}
                          className="w-10 h-10 rounded border border-gray-300 overflow-hidden shrink-0 relative group cursor-pointer bg-gray-100"
                          title="Click to view live selfie"
                        >
                          <img
                            src={resolveUploadUrl(dayItem.check_in_photo)}
                            alt="Check-in selfie"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.currentTarget;
                              if (dayItem.check_in_photo && !target.src.endsWith(dayItem.check_in_photo)) {
                                target.src = dayItem.check_in_photo;
                              }
                            }}
                          />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center">
                            <Eye className="w-3.5 h-3.5 text-white" />
                          </div>
                        </button>
                      ) : (
                        <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center text-gray-600 shrink-0">
                          <User className="w-4 h-4" />
                        </div>
                      )}

                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-mono font-black text-black">{dayItem.date}</span>
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-black uppercase text-white ${
                              dayItem.is_holiday
                                ? 'bg-blue-600'
                                : dayItem.status === 'present'
                                ? 'bg-emerald-600'
                                : dayItem.status === 'late'
                                ? 'bg-amber-600'
                                : 'bg-rose-600'
                            }`}
                          >
                            {dayItem.status}
                          </span>
                        </div>
                        {dayItem.location_name && (
                          <p className="text-[11px] text-gray-700 font-semibold truncate max-w-xs mt-0.5">
                            📍 {dayItem.location_name}
                          </p>
                        )}
                        {dayItem.check_in_distance_meters !== null && (
                          <span
                            className={`text-[10px] font-black ${
                              dayItem.check_in_distance_meters <= 30 ? 'text-emerald-700' : 'text-blue-700'
                            }`}
                          >
                            Studio distance: {dayItem.check_in_distance_meters}m (
                              {dayItem.check_in_distance_meters <= 30 ? 'On-Site' : 'Off-Site (Admin)'})
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="text-right font-mono text-[11px]">
                      <div>
                        In:{' '}
                        <span className="font-black text-black">
                          {dayItem.check_in_time ? formatTimeAMPM(dayItem.check_in_time) : '--'}
                        </span>
                      </div>
                      <div>
                        Out:{' '}
                        <span className="font-black text-black">
                          {dayItem.check_out_time ? formatTimeAMPM(dayItem.check_out_time) : '--'}
                        </span>
                      </div>
                      {(dayItem.working_seconds !== null && dayItem.working_seconds !== undefined && dayItem.working_seconds > 0) ? (
                        <div className="text-red-600 font-black font-mono">
                          {formatDurationHMS(dayItem.working_seconds, true)}
                        </div>
                      ) : dayItem.working_hours !== null ? (
                        <div className="text-red-600 font-black font-mono">
                          {formatDurationHMS(dayItem.working_hours, false)}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {/* MODAL 3: MARK AS PAID (DISBURSE SALARY) */}
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {selectedStaffForPayment && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="relative max-w-md w-full bg-white border border-gray-300 rounded-md p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-200 pb-3">
              <div>
                <h3 className="text-base font-black text-black flex items-center space-x-2">
                  <CreditCard className="w-4 h-4 text-red-600" />
                  <span>Mark Salary as Paid</span>
                </h3>
                <p className="text-xs text-gray-600 font-mono font-bold">
                  {selectedStaffForPayment.full_name} ({selectedStaffForPayment.staff_code}) • {formatMonthLabel(selectedMonth)}
                </p>
              </div>
              <button
                onClick={() => setSelectedStaffForPayment(null)}
                className="p-1 text-gray-500 hover:text-black cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Calculations Breakdown */}
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-md text-xs space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-700 font-bold">Monthly Base Salary:</span>
                <span className="font-mono font-black text-black">₹{selectedStaffForPayment.monthly_salary.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 font-bold">Payable Days (Worked + Holidays):</span>
                <span className="font-mono font-black text-black">{selectedStaffForPayment.payable_days} / {selectedStaffForPayment.total_days}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 font-bold">Calculated Base Salary:</span>
                <span className="font-mono font-black text-black">₹{selectedStaffForPayment.base_salary.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-rose-700">
                <span className="font-bold">Unpaid Advance Deducted:</span>
                <span className="font-mono font-black">-₹{selectedStaffForPayment.advance_deducted.toLocaleString()}</span>
              </div>
            </div>

            {/* Adjustment Field */}
            <div className="space-y-2">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-black text-black">
                    Manual Adjustment (+ for bonus, - for deduction):
                  </label>
                  <div className="flex items-center space-x-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        const val = Math.abs(currentAdjustmentNum) || 0;
                        setPaymentForm((prev) => ({ ...prev, adjustmentStr: `+${val}` }));
                      }}
                      className="px-2 py-0.5 text-[10px] font-black bg-emerald-100 text-emerald-800 border border-emerald-300 rounded hover:bg-emerald-200 cursor-pointer"
                    >
                      + Bonus
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const val = Math.abs(currentAdjustmentNum) || 0;
                        setPaymentForm((prev) => ({ ...prev, adjustmentStr: `-${val}` }));
                      }}
                      className="px-2 py-0.5 text-[10px] font-black bg-rose-100 text-rose-800 border border-rose-300 rounded hover:bg-rose-200 cursor-pointer"
                    >
                      - Penalty
                    </button>
                  </div>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="text"
                    value={paymentForm.adjustmentStr}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Allow typing +, -, digits and decimals without scroll/spinner arrows
                      if (/^[+\-]?[0-9]*\.?[0-9]*$/.test(val) || val === '') {
                        setPaymentForm((prev) => ({ ...prev, adjustmentStr: val }));
                      }
                    }}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-xs font-mono font-bold text-black focus:outline-none focus:border-red-600 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder="Type amount e.g. -500 or +1000"
                  />
                </div>
                <p className="text-[11px] text-gray-600 font-semibold mt-1">
                  Type negative amount with minus sign (e.g. <span className="font-mono text-rose-600 font-bold">-500</span>) or positive amount (e.g. <span className="font-mono text-emerald-600 font-bold">1000</span>). No scroll arrows.
                </p>
              </div>

              <div>
                <label className="text-xs font-black text-black block mb-1">
                  Adjustment Reason (optional):
                </label>
                <input
                  type="text"
                  value={paymentForm.adjustment_reason}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({ ...prev, adjustment_reason: e.target.value }))
                  }
                  placeholder="e.g. Late deduction penalty or performance bonus"
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-xs text-black font-semibold focus:outline-none focus:border-red-600"
                />
              </div>

              {/* Final Amount Display */}
              <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-center justify-between">
                <span className="text-xs font-black text-red-900">Net Payable Amount:</span>
                <span className="text-lg font-black font-mono text-red-600">
                  ₹
                  {Math.max(
                    0,
                    +(
                      selectedStaffForPayment.base_salary +
                      currentAdjustmentNum -
                      selectedStaffForPayment.advance_deducted
                    ).toFixed(2)
                  ).toLocaleString()}
                </span>
              </div>

              {/* Payment Mode Selection */}
              <div>
                <label className="text-xs font-black text-black block mb-1">Payment Mode:</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['upi', 'cash', 'bank'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setPaymentForm((prev) => ({ ...prev, payment_mode: mode }))}
                      className={`py-2 text-xs font-black uppercase rounded border transition-all cursor-pointer ${
                        paymentForm.payment_mode === mode
                          ? 'bg-red-600 text-white border-red-600'
                          : 'bg-white text-black border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-black text-black block mb-1">
                  Transaction Reference / UTR (optional):
                </label>
                <input
                  type="text"
                  value={paymentForm.payment_reference}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({ ...prev, payment_reference: e.target.value }))
                  }
                  placeholder="e.g. UPI Ref / Cheque No"
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-xs text-black font-mono focus:outline-none focus:border-red-600"
                />
              </div>

              <div>
                <label className="text-xs font-black text-black block mb-1">Payment Notes (optional):</label>
                <textarea
                  rows={2}
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Internal notes..."
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-xs text-black focus:outline-none focus:border-red-600"
                />
              </div>
            </div>

            <button
              onClick={handleConfirmPayment}
              disabled={recordPaymentMutation.isPending}
              className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-black text-sm rounded transition-all flex items-center justify-center space-x-2 cursor-pointer"
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>Confirm & Record Payment</span>
            </button>
          </div>
        </div>
      )}

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {/* MODAL 4: LATE OCCURRENCES DETAILS */}
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {viewLateRecordsStaff && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="relative max-w-md w-full bg-white border border-gray-300 rounded-md p-5 space-y-4">
            <div className="flex items-center justify-between border-b border-gray-200 pb-3">
              <div>
                <h3 className="text-base font-black text-black flex items-center space-x-1.5">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span>Late Check-in Log</span>
                </h3>
                <p className="text-xs text-gray-600 font-mono font-bold">
                  {viewLateRecordsStaff.full_name} • Cutoff is 10:15 AM IST
                </p>
              </div>
              <button
                onClick={() => setViewLateRecordsStaff(null)}
                className="p-1 text-gray-500 hover:text-black cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto">
              {viewLateRecordsStaff.late_records.length === 0 ? (
                <div className="text-center py-6 text-xs text-gray-500 font-bold">
                  No late check-ins recorded.
                </div>
              ) : (
                viewLateRecordsStaff.late_records.map((lr, idx) => (
                  <div
                    key={idx}
                    className="p-2.5 bg-amber-50 border border-amber-200 rounded flex items-center justify-between text-xs"
                  >
                    <div>
                      <span className="font-mono font-black text-black">{lr.date}</span>
                      <span className="text-gray-700 font-semibold ml-2">
                        Checked in at {formatTimeAMPM(lr.check_in_time)}
                      </span>
                    </div>
                    <span className="px-2 py-0.5 bg-amber-200 text-amber-900 rounded font-black text-[10px]">
                      {lr.minutes_late} min late
                    </span>
                  </div>
                ))
              )}
            </div>

            <div className="text-[11px] text-gray-600 font-semibold text-center border-t border-gray-200 pt-2">
              Admin can use the "Adjust (+/-)" field when recording payment to apply late deductions.
            </div>
          </div>
        </div>
      )}

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {/* MODAL 5: FULL PHOTO PREVIEW */}
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {viewPhotoUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setViewPhotoUrl(null)}
        >
          <div
            className="relative max-w-md w-full bg-white border border-gray-300 rounded-md overflow-hidden p-4 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-200 pb-2">
              <h3 className="text-sm font-black text-black">Attendance Verification Selfie</h3>
              <button
                onClick={() => setViewPhotoUrl(null)}
                className="p-1 text-gray-500 hover:text-black cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="aspect-[4/3] w-full rounded overflow-hidden bg-black flex items-center justify-center">
              <img
                src={resolveUploadUrl(viewPhotoUrl)}
                alt="Attendance full selfie"
                className="max-h-[70vh] w-full object-contain"
                onError={(e) => {
                  const target = e.currentTarget;
                  if (viewPhotoUrl && !target.src.endsWith(viewPhotoUrl)) {
                    target.src = viewPhotoUrl;
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
